import { Link } from 'react-router-dom';
import { ProductExplorer } from '../../components/ProductExplorer';
import { CTA, Icon3D, MicrosoftStoreButton } from '../../components/Common';
import { DemoVideo } from '../../components/DemoVideo';
import { Seo } from '../../components/Seo';
import { track } from '../../components/analytics';
import { productOffers } from '../../data/offers';
import {
  categories,
  featureGroups,
  hindiDemoVideo,
  homepageHero,
  homepageWorkflow,
  microsoftStore,
  operatingSystems,
  site,
  webApp,
} from '../../data/site';
import {
  FeatureIcon,
  Head,
  IntegrationStrip,
  OfflineFirst,
  OnboardingMigration,
  PlatformComparison,
} from './sections';

export function Home() {
  const schema = [
    { '@context': 'https://schema.org', '@type': 'WebSite', name: site.name, url: site.origin },
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: site.name,
      applicationCategory: categories.schemaOrg,
      operatingSystem: operatingSystems,
      description: site.description,
      publisher: { '@type': 'Organization', name: site.publisher },
      downloadUrl: microsoftStore.url,
      offers: productOffers(),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: site.name,
      url: site.origin,
      logo: `${site.origin}/brand/favicon-192.png`,
      email: site.demoEmail,
      sameAs: [microsoftStore.url],
    },
  ];
  return (
    <>
      <Seo
        title="Transport management software for Indian fleets"
        description={site.description}
        schema={schema}
      />
      <section className="hero">
        <div className="container hero-inner">
          <div className="hero-copy">
            <div className="eyebrow hero-kicker">
              <span className="status-dot" /> {homepageHero.kicker}
            </div>
            <h1>
              {homepageHero.headline[0]}
              <br />
              <span>{homepageHero.headline[1]}</span>
            </h1>
            <p className="lead">{homepageHero.lead}</p>
            <p className="hero-hindi" lang="hi">
              {homepageHero.hindiLine}
            </p>
            <div className="actions">
              <Link
                className="button primary"
                to="/start-trial"
                onClick={() => track('trial_cta_click', { placement: 'home_hero' })}
              >
                Start free trial
              </Link>
              <Link className="button secondary" to="/pricing">
                See pricing
              </Link>
              <MicrosoftStoreButton variant="badge" />
            </div>
            <p className="hero-alt-link">
              Nothing to install?{' '}
              <a
                href={webApp.url}
                rel="noopener"
                onClick={() => track('open_web_app', { placement: 'home_hero' })}
              >
                Open it in your browser <span aria-hidden="true">↗</span>
              </a>
            </p>
            <div className="trust-row">
              {homepageHero.proof.map(item => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-objects" aria-hidden="true">
              <Icon3D name="rupee" size={132} className="hero-object hero-object--rupee" />
              <Icon3D name="map-pin" size={120} className="hero-object hero-object--pin" />
              <Icon3D name="file-text" size={112} className="hero-object hero-object--bilty" />
            </div>
            <ProductExplorer priority />
          </div>
        </div>
      </section>

      <DemoVideo video={hindiDemoVideo} placement="home" />

      <section className="section">
        <div className="container">
          <Head
            eyebrow="One operational picture"
            title="Built around the work logistics teams already do."
            body="Move from fragmented registers and follow-up calls to a connected system for dispatch, workshop, finance, and field visibility."
          />
          <div className="bento">
            {featureGroups.map(f => (
              <article className="card bento-card" key={f.title}>
                <FeatureIcon name={f.icon} size={88} />
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <OfflineFirst />

      <section className="section surface">
        <div className="container">
          <Head
            eyebrow={homepageWorkflow.eyebrow}
            title={homepageWorkflow.title}
            body={homepageWorkflow.body}
          />
          <div className="grid four workflow">
            {homepageWorkflow.steps.map(step => (
              <article className="step" key={step.title}>
                <h3>{step.title}</h3>
                <p className="lead">{step.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <IntegrationStrip />
      <PlatformComparison />

      <section className="section dark">
        <div className="container grid two">
          <div>
            <div className="eyebrow">Controlled access</div>
            <h2>Give each team the workspace it needs.</h2>
            <p className="lead">
              Roles and module permissions separate responsibilities, while shared operational
              records keep dispatch, workshop, and finance looking at the same numbers.
            </p>
            <div className="actions">
              <Link className="button on-dark" to="/features">
                Explore features
              </Link>
            </div>
          </div>
          <div className="grid two">
            <article className="card">
              <Icon3D name="key" size={64} />
              <h3>Role-based permissions</h3>
              <p>Control viewing, editing, and administrative actions module by module.</p>
            </article>
            <article className="card">
              <Icon3D name="shield" size={64} />
              <h3>Operational continuity</h3>
              <p>
                Desktop, browser, and mobile users work from the same connected business records.
              </p>
            </article>
          </div>
        </div>
      </section>
      <OnboardingMigration />
      <CTA />
    </>
  );
}
